<template>
  <b-card no-body class="period-stats mb-4">
    <b-card-header class="border-0">
      <div class="period-stats__head">
        <div class="period-stats__title">
          <h3 class="mb-0">{{ $t('transfers.stats.title') }}</h3>
          <small class="text-muted">{{ $t('transfers.stats.hint') }}</small>
        </div>

        <div class="period-stats__range">
          <div class="period-stats__field">
            <span class="myspan">{{ $t('common.from') }}</span>
            <el-date-picker
              v-model="from"
              type="datetime"
              size="medium"
              format="dd.MM.yyyy HH:mm"
              value-format="yyyy-MM-dd HH:mm:ss"
              default-time="00:00:00"
              class="period-stats__input"
              :placeholder="$t('common.select')">
            </el-date-picker>
          </div>
          <div class="period-stats__field">
            <span class="myspan">{{ $t('common.to') }}</span>
            <el-date-picker
              v-model="to"
              type="datetime"
              size="medium"
              format="dd.MM.yyyy HH:mm"
              value-format="yyyy-MM-dd HH:mm:ss"
              default-time="00:00:00"
              class="period-stats__input"
              :placeholder="$t('common.select')">
            </el-date-picker>
          </div>
          <button
            type="button"
            class="period-stats__refresh"
            :disabled="!canFetch || loading"
            :title="$t('transfers.stats.refresh')"
            @click="fetchStats"
          >
            <i class="el-icon-refresh"></i>
          </button>
        </div>
      </div>

      <div class="period-stats__presets">
        <button
          v-for="preset in presets"
          :key="preset"
          type="button"
          class="period-stats__preset"
          @click="applyPreset(preset)"
        >
          {{ $t('transfers.stats.presets.' + preset) }}
        </button>
      </div>
    </b-card-header>

    <b-card-body class="pt-0">
      <div v-if="!from || !to" class="period-stats__placeholder">
        {{ $t('transfers.stats.selectPeriod') }}
      </div>
      <div v-else-if="rangeInvalid" class="period-stats__placeholder text-danger">
        {{ $t('transfers.stats.invalidRange') }}
      </div>
      <div v-else-if="error" class="period-stats__placeholder text-danger">
        {{ error }}
      </div>
      <div v-else-if="!stats" class="period-stats__placeholder">
        <span class="spinner-border spinner-border-sm" role="status"></span>
        <span class="ml-2">{{ $t('common.loading') }}</span>
      </div>
      <div v-else :class="{ 'period-stats__result--stale': loading }">
        <div class="period-stats__tiles">
          <div class="period-stats__tile">
            <span class="period-stats__label">{{ $t('transfers.stats.count') }}</span>
            <span class="period-stats__value">{{ stats.count }}</span>
          </div>
          <div class="period-stats__tile">
            <span class="period-stats__label">{{ $t('transfers.stats.amount') }}</span>
            <span class="period-stats__value">{{ stats.amount }} <small>UZS</small></span>
          </div>
          <div class="period-stats__tile">
            <span class="period-stats__label">{{ $t('transfers.stats.average') }}</span>
            <span class="period-stats__value">{{ stats.average }} <small>UZS</small></span>
          </div>
        </div>

        <table v-if="stats.by_type.length" class="period-stats__breakdown">
          <thead>
            <tr>
              <th>{{ $t('transfers.system') }}</th>
              <th class="text-right">{{ $t('transfers.stats.count') }}</th>
              <th class="text-right">{{ $t('transfers.amountUzs') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in stats.by_type" :key="row.type || '-'">
              <td>{{ row.type || '—' }}</td>
              <td class="text-right">{{ row.count }}</td>
              <td class="text-right">{{ row.amount }}</td>
            </tr>
          </tbody>
        </table>

        <small class="period-stats__period text-muted">
          {{ $t('transfers.stats.period', { from: stats.from, to: stats.to }) }}
        </small>
      </div>
    </b-card-body>
  </b-card>
</template>
<script>
import axios from 'axios'

// Periods are entered and counted in Tashkent time (UTC+5, no DST), whatever the browser timezone is
const TASHKENT_OFFSET_MS = 5 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

function pad(n) {
  return String(n).padStart(2, '0');
}

// Tashkent calendar day of the given moment, shifted by `days`, as yyyy-MM-dd
function tashkentDay(days) {
  const d = new Date(Date.now() + TASHKENT_OFFSET_MS + days * DAY_MS);
  return d.getUTCFullYear() + '-' + pad(d.getUTCMonth() + 1) + '-' + pad(d.getUTCDate());
}

export default {
  name: 'transfer-period-stats',
  props: {
    // 's' — sent, 'r' — received
    type: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      from: '',
      to: '',
      stats: null,
      loading: false,
      error: null,
      requestId: 0,
      presets: ['today', 'yesterday', 'week', 'month']
    }
  },
  computed: {
    // The end is exclusive on the server, so it must be strictly after the start
    rangeInvalid() {
      return !!this.from && !!this.to && this.from >= this.to;
    },
    canFetch() {
      return !!this.from && !!this.to && !this.rangeInvalid;
    },
    period() {
      return this.from + '|' + this.to;
    }
  },
  watch: {
    period() {
      // Drop the previous result and ignore any request still in flight for it
      this.requestId++;
      this.loading = false;
      this.stats = null;
      this.error = null;
      this.fetchStats();
    }
  },
  methods: {
    applyPreset(preset) {
      const today = tashkentDay(0);
      const tomorrow = tashkentDay(1);

      if (preset === 'today') {
        this.from = today + ' 00:00:00';
        this.to = tomorrow + ' 00:00:00';
      } else if (preset === 'yesterday') {
        this.from = tashkentDay(-1) + ' 00:00:00';
        this.to = today + ' 00:00:00';
      } else if (preset === 'week') {
        this.from = tashkentDay(-6) + ' 00:00:00';
        this.to = tomorrow + ' 00:00:00';
      } else if (preset === 'month') {
        this.from = today.slice(0, 8) + '01 00:00:00';
        this.to = tomorrow + ' 00:00:00';
      }
    },
    async fetchStats() {
      if (!this.canFetch) return;

      const requestId = ++this.requestId;
      this.loading = true;
      this.error = null;

      let res;
      try {
        res = await axios.post('dashboard/' + this.type + '-transfers/stats', {
          from: this.from,
          to: this.to
        })
      } catch (e) {
        if (requestId !== this.requestId) return;
        this.loading = false;
        if (e.response && e.response.status === 401) {
          await this.$router.push({name: 'login'});
          return;
        }
        this.error = this.$t('transfers.stats.loadError');
        return;
      }

      // The period was changed while this request was in flight
      if (requestId !== this.requestId) return;
      this.loading = false;

      if (res.data.status === true) {
        this.stats = res.data.stats;
      } else {
        this.error = res.data.message || this.$t('transfers.stats.loadError');
      }
    }
  }
}
</script>

<style scoped lang="scss">
.period-stats__head {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.period-stats__range {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
}

.period-stats__field {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.period-stats__input {
  width: 190px !important;
}

.period-stats__refresh {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  color: #5e72e4;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) {
    background: rgba(94, 114, 228, 0.08);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.period-stats__presets {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.period-stats__preset {
  padding: 0.25rem 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 999px;
  background: #f6f9fc;
  color: #525f7f;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(94, 114, 228, 0.08);
    border-color: rgba(94, 114, 228, 0.3);
  }
}

.period-stats__placeholder {
  padding: 1.5rem 0;
  text-align: center;
  color: #8898aa;
  font-size: 0.9rem;
}

.period-stats__result--stale {
  opacity: 0.5;
  transition: opacity 0.15s ease;
}

.period-stats__tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.period-stats__tile {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 1rem 1.25rem;
  border-radius: 6px;
  background: #f6f9fc;
}

.period-stats__label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #8898aa;
}

.period-stats__value {
  font-size: 1.35rem;
  font-weight: 700;
  color: #32325d;
  font-variant-numeric: tabular-nums;

  small {
    font-size: 0.75rem;
    font-weight: 500;
    color: #8898aa;
  }
}

.period-stats__breakdown {
  width: 100%;
  margin-top: 1rem;
  font-size: 0.85rem;
  font-variant-numeric: tabular-nums;

  th {
    padding: 0.5rem 0.75rem;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    color: #8898aa;
    background: #f6f9fc;
  }

  td {
    padding: 0.5rem 0.75rem;
    color: #525f7f;
    border-top: 1px solid #e9ecef;
  }
}

.period-stats__period {
  display: block;
  margin-top: 0.75rem;
}

@media only screen and (max-width: 768px) {
  .period-stats__range,
  .period-stats__field {
    width: 100%;
  }

  .period-stats__input {
    flex: 1;
    width: auto !important;
  }
}
</style>
